// src/pages/UnitPolicies.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Spinner, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';

const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

// ── Modal para editar política de una unidad ───────────────────────────────────
function PolicyModal({ unidad, existing, allDepts, onClose, onSaved }) {
    const [selected, setSelected] = useState(
        existing?.departments?.map(d => d._id || d) || []
    );
    const [saving, setSaving] = useState(false);

    const toggle = (id) =>
        setSelected(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(
                `/unit-policies/${encodeURIComponent(unidad)}`,
                { departments: selected }
            );
            toast.success(`Política de "${unidad}" guardada`);
            onSaved(data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const handleRemove = async () => {
        if (!window.confirm(`¿Eliminar la política de "${unidad}"? La unidad verá todos los departamentos.`)) return;
        setSaving(true);
        try {
            await api.delete(`/unit-policies/${encodeURIComponent(unidad)}`);
            toast.success('Política eliminada');
            onSaved(null);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al eliminar');
        } finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontWeight: 700, fontSize: 15 }}>
                    Departamentos visibles — <span style={{ color: '#2563eb' }}>{unidad}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: 13, color: 'var(--c-text-3)', marginBottom: 12 }}>
                    Selecciona los departamentos a los que pueden enviar tickets los usuarios de esta unidad.
                    Si no seleccionas ninguno, verán todos.
                </p>
                <div style={{
                    border: '1px solid var(--c-border)', borderRadius: 8,
                    padding: '10px 14px', maxHeight: 280, overflowY: 'auto',
                    background: 'var(--c-input-bg)',
                }}>
                    {allDepts.map(d => (
                        <Form.Check
                            key={d._id}
                            type="checkbox"
                            id={`dept-${d._id}`}
                            label={d.name}
                            checked={selected.includes(d._id)}
                            onChange={() => toggle(d._id)}
                            style={{ fontSize: 13, padding: '3px 0' }}
                        />
                    ))}
                </div>
                {selected.length === 0 && (
                    <p style={{ fontSize: 12, color: 'var(--c-text-4)', marginTop: 8, marginBottom: 0 }}>
                        Sin selección — la unidad verá todos los departamentos.
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                <div>
                    {existing && (
                        <Button variant="outline-danger" size="sm" onClick={handleRemove} disabled={saving} style={{ borderRadius: 8 }}>
                            Quitar política
                        </Button>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="secondary" onClick={onClose} disabled={saving} style={{ borderRadius: 8 }}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving} style={{ borderRadius: 8 }}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Guardar'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

// ── Página principal ───────────────────────────────────────────────────────────
export default function UnitPolicies() {
    const [units,    setUnits]    = useState([]);
    const [policies, setPolicies] = useState([]);
    const [depts,    setDepts]    = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [editing,  setEditing]  = useState(null); // unidad string

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [u, p, d] = await Promise.all([
                api.get('/unit-policies/units'),
                api.get('/unit-policies'),
                api.get('/departments'),
            ]);
            setUnits(u.data);
            setPolicies(p.data);
            setDepts(d.data);
        } catch {
            toast.error('Error al cargar datos');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const policyFor = (unidad) => policies.find(p => p.unidad === unidad);

    const handleSaved = (updated) => {
        if (updated === null) {
            // eliminada
            setPolicies(prev => prev.filter(p => p.unidad !== editing));
        } else {
            setPolicies(prev => {
                const exists = prev.find(p => p.unidad === updated.unidad);
                return exists
                    ? prev.map(p => p.unidad === updated.unidad ? updated : p)
                    : [...prev, updated];
            });
        }
        setEditing(null);
    };

    const configured = units.filter(u => policyFor(u));
    const unrestricted = units.filter(u => !policyFor(u));

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>

            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontWeight: 700, margin: 0, color: 'var(--c-text-1)' }}>Políticas por Unidad</h4>
                <p style={{ color: 'var(--c-text-3)', margin: '4px 0 0', fontSize: 14 }}>
                    Define qué departamentos puede ver cada unidad de negocio al crear un ticket.
                </p>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
                {[
                    { label: 'Unidades totales',    value: units.length,       color: '#2563eb' },
                    { label: 'Con política',         value: configured.length,  color: '#059669' },
                    { label: 'Sin restricción',      value: unrestricted.length, color: '#d97706' },
                    { label: 'Departamentos',        value: depts.length,       color: '#7c3aed' },
                ].map(k => (
                    <div key={k.label} className="card-hover" style={{ ...CARD, borderTop: `4px solid ${k.color}`, padding: '14px 18px' }}>
                        <p style={LABEL}>{k.label}</p>
                        <p style={{ fontSize: 28, fontWeight: 800, color: 'var(--c-text-1)', margin: '4px 0 0', lineHeight: 1 }}>{k.value}</p>
                    </div>
                ))}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}><Spinner animation="border" /></div>
            ) : (
                <div style={CARD}>
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-2)' }}>
                            {units.length} unidades registradas en el sistema
                        </span>
                    </div>

                    {units.length === 0 ? (
                        <p style={{ textAlign: 'center', color: 'var(--c-text-4)', padding: '48px 0', margin: 0 }}>
                            Aún no hay usuarios de intranet con unidad registrada.
                        </p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: 'var(--c-msg-bg)' }}>
                                    {['Unidad de negocio', 'Departamentos permitidos', 'Estado', 'Acción'].map(h => (
                                        <th key={h} style={{ padding: '10px 20px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)', textAlign: 'left' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {units.map(unidad => {
                                    const policy = policyFor(unidad);
                                    return (
                                        <tr key={unidad} style={{ borderBottom: '1px solid var(--c-border)' }}>
                                            <td style={{ padding: '12px 20px', fontWeight: 600, color: 'var(--c-text-1)' }}>
                                                {unidad}
                                            </td>
                                            <td style={{ padding: '12px 20px', color: 'var(--c-text-2)' }}>
                                                {policy ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                        {policy.departments.map(d => (
                                                            <span key={d._id} style={{
                                                                fontSize: 11, fontWeight: 600,
                                                                background: 'var(--c-status-open-bg)',
                                                                color: '#2563eb',
                                                                border: '1px solid #bfdbfe',
                                                                borderRadius: 20, padding: '2px 8px',
                                                            }}>
                                                                {d.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: 'var(--c-text-4)', fontStyle: 'italic', fontSize: 12 }}>
                                                        Todos los departamentos
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px 20px' }}>
                                                <span style={{
                                                    fontSize: 11, fontWeight: 600, borderRadius: 20,
                                                    padding: '2px 10px',
                                                    background: policy ? '#ecfdf5' : '#fffbeb',
                                                    color:      policy ? '#059669' : '#d97706',
                                                }}>
                                                    {policy ? 'Restringida' : 'Sin restricción'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 20px' }}>
                                                <Button
                                                    size="sm"
                                                    variant={policy ? 'outline-primary' : 'outline-secondary'}
                                                    onClick={() => setEditing(unidad)}
                                                    style={{ borderRadius: 8, fontSize: 12 }}
                                                >
                                                    {policy ? 'Editar' : 'Configurar'}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {editing && (
                <PolicyModal
                    unidad={editing}
                    existing={policyFor(editing)}
                    allDepts={depts}
                    onClose={() => setEditing(null)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}
